import React, { useState, useEffect } from 'react';
import axios from 'axios';
import constant from '../constants/constant';
import { Layers, RefreshCw } from 'lucide-react';

const CommissionsTracking = () => {
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommissions();
  }, []);

  const fetchCommissions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${constant.API_BASE_URL}/admin/rewards/commissions`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) setCommissions(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (id, status) => {
    if (window.confirm(`Are you sure you want to mark this commission as ${status}?`)) {
      try {
        await axios.put(`${constant.API_BASE_URL}/admin/rewards/commissions/${id}/status`, { status }, {
          headers: { Authorization: sessionStorage.getItem('adminToken') }
        });
        fetchCommissions();
      } catch (err) {
        alert("Failed to update status");
      }
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">Pending Commissions</h2>
          <p className="text-slate-500 mt-1">Track 10-day locked cascading commissions for Agencies, BDs, and Managers.</p>
        </div>
        <button onClick={fetchCommissions} className="flex items-center gap-2 bg-white text-slate-700 px-5 py-2.5 rounded-xl font-semibold shadow-sm border border-gray-200 hover:bg-gray-50 transition">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-100/30 border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-20 text-center text-slate-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">Beneficiary (Role)</th>
                  <th className="p-6">Source User</th>
                  <th className="p-6">Target Level</th>
                  <th className="p-6">Amount (💎)</th>
                  <th className="p-6">Release Date</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {commissions.map(comm => (
                  <tr key={comm.id} className="hover:bg-slate-50/50 transition">
                    <td className="p-6 font-bold text-slate-800">
                      {comm.ben_first} {comm.ben_last}
                      <p className="text-xs font-black text-pink-600 mt-0.5">{comm.role_type}</p>
                    </td>
                    <td className="p-6 text-slate-600">
                      {comm.src_first} {comm.src_last}
                    </td>
                    <td className="p-6 text-slate-600">Level {comm.target_level}</td>
                    <td className="p-6 font-extrabold text-pink-600">💎 {parseInt(comm.amount).toLocaleString()}</td>
                    <td className="p-6 text-slate-600">{new Date(comm.release_date).toLocaleString()}</td>
                    <td className="p-6">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        comm.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                        comm.status === 'CREDITED' ? 'bg-green-100 text-green-700' :
                        comm.status === 'HOLD' ? 'bg-orange-100 text-orange-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {comm.status}
                      </span>
                    </td>
                    <td className="p-6 text-right space-x-2">
                      {comm.status === 'PENDING' && (
                        <>
                          <button onClick={() => handleUpdateStatus(comm.id, 'HOLD')} className="px-3 py-1.5 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 text-xs font-bold transition">Hold</button>
                          <button onClick={() => handleUpdateStatus(comm.id, 'CANCELLED')} className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-xs font-bold transition">Cancel</button>
                        </>
                      )}
                      {comm.status === 'HOLD' && (
                        <button onClick={() => handleUpdateStatus(comm.id, 'PENDING')} className="px-3 py-1.5 bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 text-xs font-bold transition">Un-hold</button>
                      )}
                    </td>
                  </tr>
                ))}
                {commissions.length === 0 && (
                  <tr><td colSpan="7" className="p-10 text-center text-slate-500">No commissions pending.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionsTracking;
